import languageCodes from './reddit-language-codes.json';

export type AccountPostSource = 'facebook' | 'instagram';
export type AccountPostMediaType = 'image' | 'video' | 'carousel' | 'reel' | 'text';
export interface MarketAccountPostFilters {
    match_mode?: 'all_account_posts' | 'keywords';
    published_from?: string;
    published_to?: string;
    media_types?: AccountPostMediaType[];
    languages?: string[];
    min_likes?: number;
    min_reactions?: number;
    min_comments?: number;
    min_shares?: number;
    min_video_views?: number;
    min_plays?: number;
}
export const accountMetricKeys = (source: AccountPostSource) => source === 'facebook'
    ? ['min_reactions', 'min_comments', 'min_shares', 'min_video_views', 'min_plays'] as const
    : ['min_likes', 'min_comments', 'min_video_views'] as const;
export const accountMediaTypes = (source: AccountPostSource): AccountPostMediaType[] =>
    source === 'facebook' ? ['image', 'video', 'carousel', 'reel', 'text'] : ['image', 'video', 'carousel', 'reel'];

/** Also used for preview negotiation: ignored, altered and uncleared conditions must fail closed. */
export function normalizeAccountPostFilters(source: AccountPostSource, input: unknown): MarketAccountPostFilters {
    if (input == null) return {};
    if (typeof input !== 'object' || Array.isArray(input)) throw new Error('Invalid account post filters');
    const filters = input as Record<string, unknown>;
    const metrics = accountMetricKeys(source);
    const keys = new Set<string>(['match_mode', 'published_from', 'published_to', 'media_types', 'languages', ...metrics]);
    if (Object.keys(filters).some(key => !keys.has(key))) throw new Error('Unsupported account post filter');
    const result: MarketAccountPostFilters = {};
    if ('match_mode' in filters) {
        if (!['all_account_posts', 'keywords'].includes(String(filters.match_mode))) throw new Error('Invalid matching mode');
        if (filters.match_mode === 'keywords') result.match_mode = 'keywords';
    }
    for (const key of ['published_from', 'published_to'] as const) if (key in filters) {
        const value = filters[key];
        const date = typeof value === 'string' ? new Date(value + 'T00:00:00Z') : new Date(NaN);
        if (typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value) || !Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value)
            throw new Error('Invalid UTC publication date');
        result[key] = value;
    }
    if (result.published_from && result.published_to && result.published_from > result.published_to) throw new Error('Invalid date range');
    for (const key of ['media_types', 'languages'] as const) if (key in filters) {
        const values = filters[key];
        const allowed: readonly string[] = key === 'languages' ? languageCodes : accountMediaTypes(source);
        if (!Array.isArray(values) || values.length > allowed.length || values.some(value => typeof value !== 'string')) throw new Error('Invalid filter list');
        const normalized = [...new Set(values.map(value => key === 'languages' ? value.trim().toLowerCase() : value))].sort();
        if (normalized.some(value => !allowed.includes(value))) throw new Error('Unsupported filter value');
        if (normalized.length) Object.assign(result, { [key]: normalized });
    }
    for (const key of metrics) if (key in filters) {
        const value = filters[key];
        if (typeof value !== 'number' || !Number.isSafeInteger(value) || value < 0) throw new Error('Invalid interaction minimum');
        result[key] = value;
    }
    return result;
}
